<template>
  <Modal
    v-if="member && organisationAndMembers"
    class="organisation-member-removal-dialog text-left"
    v-model="shown"
    :title="
      'Move ' + member.shortUserDetails.firstName + ' ' + member.shortUserDetails.lastName + ''
    "
  >
    <div style="width: 100%">
      <p class="text-big">
        Which organisation would you like to move
        <b>{{ member.shortUserDetails.firstName }} {{ member.shortUserDetails.lastName }}</b> to?
      </p>
      <p>
        <span class="text-warning">Note:</span> They will also be removed from any <b>Teams</b> and
        <b>Groups</b> in this organisation.
      </p>
      <div class="row">
        <div class="col-sm-12">
          <organisation-picker
            v-if="shown"
            :organisations="allOrganisations"
            :selection="selectedOrganisation"
            @selection-changed="onSelectionChanged"
          >
          </organisation-picker>
        </div>
      </div>
      <div class="row" v-if="errors && errors.length">
        <br />
        <div class="col-sm-12">
          <p class="col-sm-12 text-danger text-left" v-for="error in errors" v-bind:key="error">
            {{ error }}
          </p>
        </div>
      </div>
    </div>
    <template #footer>
      <div>
        <button
          class="btn btn-primary btn-lg"
          @click="doMove()"
          :disabled="!selectedOrganisation"
          type="button"
        >
          Move
        </button>

        <button class="btn btn-default btn-lg" @click="cancel()" type="button">Cancel</button>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import axios from "axios";
import { Modal } from "uiv";
import OrganisationPicker from "./organisation-picker.vue";
import {
  OrganisationAndMembersApiModel,
  OrganisationDescriptionApiModel,
  OrganisationMemberApiModel,
  OrganisationMemberUpdateApiModel,
} from "../../../api-models";

const resolveCallback = ref<((value: boolean) => void) | null>(null);
const rejectCallback = ref<((reason?: any) => void) | null>(null);

const errors = ref<string[]>([]);
const member = ref<OrganisationMemberApiModel | null>(null);
const organisationAndMembers = ref<OrganisationAndMembersApiModel | null>(null);
const allOrganisations = ref<OrganisationDescriptionApiModel[]>([]);
const selectedOrganisation = ref<OrganisationDescriptionApiModel | null>(null);
const shown = ref(false);

const close = () => {
  shown.value = false;
};

const cancel = () => {
  close();
  if (resolveCallback.value) {
    resolveCallback.value(false);
  }
};

const open = (
  memberValue: OrganisationMemberApiModel,
  organisationAndMembersValue: OrganisationAndMembersApiModel,
  allOrganisationsValue: OrganisationDescriptionApiModel[],
): Promise<boolean> => {
  member.value = memberValue;
  organisationAndMembers.value = organisationAndMembersValue;
  allOrganisations.value = allOrganisationsValue.filter(
    (o) => o.id !== organisationAndMembersValue.id,
  );
  selectedOrganisation.value = null;
  errors.value = [];
  shown.value = true;
  return new Promise((resolve, reject) => {
    resolveCallback.value = resolve;
    rejectCallback.value = reject;
  });
};

const doMove = () => {
  if (!selectedOrganisation.value || !member.value) {
    return;
  }

  const data: OrganisationMemberUpdateApiModel = {
    userId: member.value?.shortUserDetails.id,
    organisationId: selectedOrganisation.value.id,
    role: "Member",
  };
  axios
    .post("/api/organisationmember", data)
    .then((res) => {
      if (res.status !== 200) {
        errors.value = ["There was a problem moving the user, please try again."];
      } else {
        errors.value = [];
        close();
        if (resolveCallback.value) {
          resolveCallback.value(true);
        }
      }
    })
    .catch((error) => {
      errors.value = ["There was a problem moving the user, please try again."];
      if (error?.response?.data?.length) {
        errors.value.push(error.response.data);
      }
    });
};

const onSelectionChanged = (newSelection: OrganisationDescriptionApiModel): void => {
  selectedOrganisation.value = newSelection;
};

defineExpose({
  open,
  close,
  cancel,
});
</script>
